import type {
  AiChatContentItem,
  ChatSessionDetailMessage,
  ChatSessionDetailRsp,
  ChatSessionDetailTurn,
  ChatSessionDetailWire,
} from '@/api/ai-chat';
import {
  getChatSessionDetail,
  getTrainingProposal,
  isTrainingProposalNotFound,
} from '@/api/ai-chat';

import { artifactsFromBlocks } from '../util/artifacts';
import {
  createCustomerCardCache,
  fillCustomerSessionPreview,
} from '../util/customerSessionCard';
import type { SessionMessage } from '../util/message';
import { isRecord, readString } from '../util/parse';
import {
  queriedAcceptance,
  readProposalWireFlags,
} from '../util/proposalAcceptance';
import {
  clarificationsFromBlocks,
  proposalFromBlocks,
  readProposalChanges,
  replayPartsFromBlocks,
  traceFromBlocks,
} from '../util/replay';
import { strategyKeyFromType, toSessionMessages } from '../util/session';
import {
  customerPreviewFromSourceRef,
  isShareSessionId,
} from '../util/sessionPath';
import type { ChatApiStrategyKey } from './types';

/**
 * streaming 会话必须取到最后一轮，供详情回放和 resume 共用同一条助手消息。
 * 是否补页只为取得 resume 协议必需的 turn_id，不参与是否续传的业务判断。
 */
export const loadSessionDetail = async (
  appId: string,
  sessionId: string
): Promise<{
  payload: ChatSessionDetailWire;
  resumeTurnId: string;
}> => {
  const first = await getChatSessionDetail({
    app_id: appId,
    session_id: sessionId,
    page: 1,
    page_size: 100,
  });
  const shouldResume = first.session.last_status === 'streaming';
  if (!shouldResume) return { payload: first, resumeTurnId: '' };

  const pageSize = first.page_size || 100;
  const pageCount = Math.ceil(first.total / pageSize);
  const rest =
    pageCount <= 1
      ? []
      : await Promise.all(
          Array.from({ length: pageCount - 1 }, (_, index) =>
            getChatSessionDetail({
              app_id: appId,
              session_id: sessionId,
              page: index + 2,
              page_size: pageSize,
            })
          )
        );
  const payload = {
    ...first,
    turns: [first, ...rest].flatMap((page) => page.turns ?? []),
  };
  // 分享页不续传，避免 SSE 帧带回真实 session_id。历史页仍然补齐。
  if (isShareSessionId(sessionId)) return { payload, resumeTurnId: '' };
  const resumeTurnId = payload.turns[payload.turns.length - 1]?.turn_id;
  if (!resumeTurnId) {
    throw new Error(
      `streaming session ${payload.session.session_id} is missing turn_id`
    );
  }
  return { payload, resumeTurnId };
};

const SYSTEM_ERROR_REPLY = '系统异常，请稍后重试。';

/** 10 位及以下按秒，13 位毫秒字符串按毫秒。 */
const toMillis = (value: number | string) => {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return 0;
  return timestamp < 1e12 ? timestamp * 1000 : timestamp;
};

const toIso = (value: number | string) =>
  new Date(toMillis(value)).toISOString();

/** 已结束轮的耗时秒数。结束早于开始时取 0。 */
const turnTimeUsedSeconds = (turn: ChatSessionDetailTurn) =>
  Math.max(
    0,
    Math.floor((toMillis(turn.updated_at) - toMillis(turn.created_at)) / 1000)
  );

const userText = (items: AiChatContentItem[]) =>
  items
    .map((item) => item.content?.trim() || '')
    .filter(Boolean)
    .join('\n');

const userAttachments = (items: AiChatContentItem[]) =>
  items.flatMap((item) =>
    (item.media ?? []).flatMap((media) => {
      if (!media.name && !media.url) return [];
      return [
        {
          url: media.url,
          name: media.name,
          mime_type: media.mime_type,
          size: media.size,
        },
      ];
    })
  );

const sortedBlocks = (turn: ChatSessionDetailTurn) =>
  [...(turn.response ?? [])].sort((left, right) => left.index - right.index);

const hasSystemReply = (message: ChatSessionDetailMessage) =>
  message.type === 'assistant' &&
  Boolean(
    message.content.trim() ||
      message.progress?.trim() ||
      message.trace?.length ||
      message.clarifications?.length ||
      message.proposal ||
      message.replay?.length ||
      message.artifacts?.length
  );

const fallbackAssistant = (
  sessionId: string,
  roundNumber: number,
  turn: ChatSessionDetailTurn
): ChatSessionDetailMessage => ({
  id: `${sessionId}-a${roundNumber}`,
  type: 'assistant',
  content: SYSTEM_ERROR_REPLY,
  roundNumber,
  createdAt: toIso(turn.updated_at),
  status: 'done',
  timeUsed: turnTimeUsedSeconds(turn),
});

const toUserMessage = (
  sessionId: string,
  roundNumber: number,
  turn: ChatSessionDetailTurn
): ChatSessionDetailMessage => {
  const attachments = userAttachments(turn.request.items);
  const sourcePreview = customerPreviewFromSourceRef(turn.request.source_ref);
  return {
    id: `${sessionId}-u${roundNumber}`,
    type: 'user',
    content: userText(turn.request.items),
    roundNumber,
    createdAt: toIso(turn.created_at),
    status: 'done',
    ...(attachments.length ? { attachments } : {}),
    ...(sourcePreview ? { sourcePreview } : {}),
  };
};

const toAssistantMessage = (
  sessionId: string,
  roundNumber: number,
  turn: ChatSessionDetailTurn
): ChatSessionDetailMessage => {
  const blocks = sortedBlocks(turn);
  const reasoning = blocks.filter((block) => block.type === 'reasoning');
  const texts = blocks.filter((block) => block.type === 'text');
  const streaming = turn.status === 'streaming';
  const message: ChatSessionDetailMessage = {
    id: `${sessionId}-a${roundNumber}`,
    type: 'assistant',
    content: texts.map((block) => block.text).join('\n\n'),
    roundNumber,
    // 续联用 turn 创建时间做累加起点；已结束仍用 updated_at。
    createdAt: toIso(streaming ? turn.created_at : turn.updated_at),
    status: streaming ? 'streaming' : 'done',
    ...(streaming ? {} : { timeUsed: turnTimeUsedSeconds(turn) }),
  };
  if (reasoning.length) {
    message.progress = reasoning.map((block) => block.text).join('\n');
  }
  const trace = traceFromBlocks(blocks);
  if (trace.some((item) => item.kind === 'tool')) message.trace = trace;
  // 历史块写到消息字段。输入区 choices 只读最后一条的 clarifications / proposal。
  const clarifications = clarificationsFromBlocks(blocks);
  if (clarifications.length) message.clarifications = clarifications;
  const picked = proposalFromBlocks(blocks);
  if (picked) message.proposal = picked.proposal;
  const artifacts = artifactsFromBlocks(blocks);
  if (artifacts.length) message.artifacts = artifacts;
  const replay = replayPartsFromBlocks(blocks);
  if (replay.length) message.replay = replay;
  // 进行中的轮次把原始块交给 SSE 续写，后面的增量按 index 接上，不重头再拼。
  if (turn.status === 'streaming' && blocks.length) {
    message.blocks = blocks.map((block) => ({
      index: block.index,
      type: block.type,
      text: block.text,
      ...(block.extra ? { extra: block.extra } : {}),
    }));
  }
  return message;
};

const toDetailMessages = (
  sessionId: string,
  turns: ChatSessionDetailTurn[],
  page: number,
  pageSize: number
): ChatSessionDetailMessage[] =>
  turns.flatMap((turn, turnIndex) => {
    const roundNumber = (page - 1) * pageSize + turnIndex + 1;
    const user = toUserMessage(sessionId, roundNumber, turn);
    const assistant = toAssistantMessage(sessionId, roundNumber, turn);
    const keepPartial =
      turn.status === 'streaming' || hasSystemReply(assistant);
    return [
      user,
      keepPartial ? assistant : fallbackAssistant(sessionId, roundNumber, turn),
    ];
  });

interface ProposalOperator {
  appId: string;
  userId: string;
  signal?: AbortSignal;
}

const looksLikeProposal = (item: Record<string, unknown>) =>
  Boolean(
    readString(item.proposal_id) ||
      (readString(item.status) && Array.isArray(item.changes))
  );

/**
 * query-proposal 的正文有时就是提案，有时包在 data / Data 里。
 * 优先认带 proposal_id 的那一层，避免外层 status 把 accepted 盖掉。
 */
const proposalPayload = (value: unknown) => {
  if (!isRecord(value)) return undefined;
  const nested = [value.data, value.Data].find(isRecord);
  let source: Record<string, unknown> | undefined;
  if (looksLikeProposal(value)) source = value;
  else if (nested && looksLikeProposal(nested)) source = nested;
  else if (readString(value.status)) source = value;
  else if (nested && readString(nested.status)) source = nested;
  if (!source) return undefined;
  const wire = readProposalWireFlags(source);
  if (!wire.status && !wire.selectedAction && !wire.locked) return undefined;
  return {
    ...wire,
    changes: Array.isArray(source.changes) ? source.changes : undefined,
  };
};

/**
 * 聊天记录里的提案快照一般是 draft。
 * 只查最新一轮对话的提案状态：
 * - 查到 accepted / online_applied，或 selected_action 为 direct_accept，标成采纳成功。
 * - 只要不是返回 404（含查询成功或非 404 错误），一律置 locked: true 隐藏输入区选项卡。
 * - 404 且 PROPOSAL_NOT_FOUND 时保持未锁定，供用户继续选择。
 * 进行中的那一轮不查。
 */
const refreshLatestProposal = async (
  proposal: NonNullable<ChatSessionDetailMessage['proposal']>,
  streaming: boolean,
  operator: ProposalOperator,
  hasMore: boolean
) => {
  if (hasMore) return { ...proposal, locked: true };
  if (streaming || !proposal.proposalId) return proposal;
  try {
    const lookedUp = proposalPayload(
      await getTrainingProposal({
        app_id: operator.appId,
        user_id: operator.userId,
        proposal_id: proposal.proposalId,
      })
    );
    if (!lookedUp) return { ...proposal, locked: true };
    const next = queriedAcceptance(proposal.status, lookedUp);
    return {
      ...proposal,
      status: next.status,
      locked: true,
      selectedAction: next.selectedAction,
      ...(lookedUp.changes
        ? { changes: readProposalChanges(lookedUp.changes) }
        : {}),
    };
  } catch (error) {
    if (!isTrainingProposalNotFound(error)) {
      return { ...proposal, locked: true };
    }
    return proposal;
  }
};

/** 把详情接口的 session + turns 映射成聊天窗的 user / assistant 消息。 */
export const toSessionDetail = async (
  payload: ChatSessionDetailWire,
  operator: ProposalOperator
): Promise<ChatSessionDetailRsp> => {
  const sessionId = payload.session.session_id;
  const page = payload.page ?? 1;
  const pageSize = payload.page_size ?? 100;
  const turns = payload.turns ?? [];
  const loaded = (page - 1) * pageSize + turns.length;
  const hasMore = loaded < (payload.total ?? loaded);
  const messages = toDetailMessages(sessionId, turns, page, pageSize);
  const cardCache = createCustomerCardCache();
  const lastIndex = messages.length - 1;
  return {
    session_id: sessionId,
    title: payload.session.title,
    messages: await Promise.all(
      messages.map(async (message, index) => {
        const withCard = message.sourcePreview
          ? {
              ...message,
              sourcePreview: await fillCustomerSessionPreview(
                message.sourcePreview,
                operator.appId,
                cardCache
              ),
            }
          : message;
        if (index !== lastIndex || !withCard.proposal) return withCard;
        const proposal = await refreshLatestProposal(
          withCard.proposal,
          withCard.status === 'streaming',
          operator,
          hasMore
        );
        return { ...withCard, proposal };
      })
    ),
  };
};

export interface ListedSessionDetailResult {
  title: string;
  messages: SessionMessage[];
  resumeTurnId: string;
  strategyKey?: ChatApiStrategyKey;
  lastStatus: 'streaming' | 'completed' | 'failed';
}

/** 详情表示会话不存在。分享 token 过期或被重置时也是这句。 */
export const isSessionMissingError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /session not found/i.test(message);
};

/** 拉取侧栏会话详情并映射为前端消息（不含 store 竞态）。 */
export const loadListedSessionDetail = async (
  appId: string,
  sessionId: string,
  userId: string
): Promise<ListedSessionDetailResult> => {
  const { payload, resumeTurnId } = await loadSessionDetail(appId, sessionId);
  const rsp = await toSessionDetail(payload, { appId, userId });
  return {
    title: rsp.title || '',
    messages: toSessionMessages(rsp.messages),
    resumeTurnId,
    strategyKey: strategyKeyFromType(payload.session.session_type),
    lastStatus: payload.session.last_status,
  };
};
